import type { ItemId } from '@/data/items';

/**
 * Abilities absorbed into a weapon.
 *
 * The Dark Cloud 2 idea the README points at: what you killed goes into what
 * you killed it with. Moth dust and blight sap already drop and are already
 * spent on forging, so absorption gives them a second use that competes with
 * the first — materials you feed a blade are materials you are not forging
 * the other blade with.
 *
 * Both abilities change what a hit *does* rather than what it is worth. A
 * "+2 damage" absorption would be a level by another name, and levels already
 * exist.
 */

export type AbilityId = 'spiritDraw' | 'sapping';

export interface AbilityDefinition {
  readonly id: AbilityId;
  readonly name: string;
  readonly note: string;
  /** What is fed in, and how much of it. */
  readonly material: ItemId;
  readonly cost: number;
}

export const ABILITIES: Readonly<Record<AbilityId, AbilityDefinition>> = {
  // Melee that feeds magic. Turns the Spirit bar from something you spend
  // between fights into something a fight refills.
  spiritDraw: {
    id: 'spiritDraw',
    name: 'Spirit Draw',
    note: 'Every hit returns a little Spirit.',
    material: 'mat:mothDust',
    cost: 8,
  },
  // The answer to being chased. A slow blade that lands sapping hits stops
  // being a slow blade in the way that matters.
  sapping: {
    id: 'sapping',
    name: 'Sapping',
    note: 'Hits slow what they land on.',
    material: 'mat:blightSap',
    cost: 6,
  },
};

/** Spirit returned per hit by Spirit Draw. */
export const SPIRIT_PER_HIT = 1.5;

/** Seconds a sapped enemy stays slowed, and how much of its speed it keeps. */
export const SAP_SECONDS = 1.6;
export const SAP_SPEED = 0.45;

/**
 * How many abilities one weapon can hold.
 *
 * Two, so absorbing is a decision. With room for every ability there is no
 * choice to make, and the materials would only ever be a queue.
 */
export const ABILITY_SLOTS = 2;

export const ABILITY_IDS = Object.keys(ABILITIES) as AbilityId[];

export function isAbilityId(value: unknown): value is AbilityId {
  return typeof value === 'string' && value in ABILITIES;
}
